import operations from '~/helpers/device-operations';
import { subtype, wifiChannel, zigBeeAddress } from '~/helpers/fields-description';
import { Operation } from '~/interfaces/device.interface';
import { WirelessSwitch } from '~/models/modules/legrand/WirelessSwitch.model';
import apiService from '~/services/api.service';
import { ConfigModule } from '~/store/modules/config';
import { DeviceGroupModule } from '~/store/modules/devicegroup';
import { DevicesModule } from '~/store/modules/devices';
import { LegrandGateway } from './LegrandGateway.model';

export class NLG extends LegrandGateway {

    MAX_TIME_BEFORE_UNKNOWN = 6 * 3600;
    shouldFetchSyncData = true;

    // TODO: generated constants?
    switchDatabaseAllowedGroups = [
        'nlg_alpha',
        'nlg_dev',
        'nlg_sandbox',
        'nlg_sandbox2',
        'nlg_no_update',
        'nlg_validation',
    ];

    get fieldZigBeeAddress() {
        return zigBeeAddress(this.zigbee_address);
    }
    get fieldSubtype() {
        return subtype(this.subtype || 'Legrand');
    }
    get fieldWifiChannel() {
        return wifiChannel(this.wifi_channel);
    }

    get deviceOperations(): Operation[]  {
        return super.deviceOperations.concat([
            operations.modelDump({ callback: this.modelDump }),
            operations.switchDatabase({
                callback: this.switchDatabase,
                disabled: this.isSwitchDatabaseDisabled,
            }),
            operations.dump({ callback: this.dump }),
            operations.orderReboot({ callback: this.orderReboot, disabled: this.isOrderRebootDisabled }),
            operations.setDump({ callback: this.setDump }),
            operations.resetFlag({ callback: this.resetFlag }),
        ]);
    }

    async loadSyncData() {
        // check if any of the modules is a wireless switch, if not return
        if (!this.modules_bridged?.find((moduleId) => {
            const moduleObject = DevicesModule.getDeviceById(moduleId);
            return moduleObject instanceof WirelessSwitch || ['Z3L', 'Z3V'].includes(moduleObject?.type || '');
        })) {
            return;
        }
        // we have at least one wireless switch so we do getConfigs to get the switch binding infos
        const configs = await apiService.getConfigs({ device_id: this.id });

        // if module has either endpoints of bindings defined, it means we have binding info
        const modulesWithEndpoints = configs.data.body.home.modules.filter((moduleWithEndpoint: any) => {
            return typeof moduleWithEndpoint.endpoints !== 'undefined' || typeof moduleWithEndpoint.bindings !== 'undefined';
        });

        // iterate over modules with endpoints found in the getConfigs
        modulesWithEndpoints.forEach((moduleWithEndpoint: any) => {
            // find the corresponding module in our device modules
            const currModule = this.modules_bridged?.find((moduleId) => {
                return moduleWithEndpoint.id === moduleId;
            });

            // if endpoints is not defined, we want to define it ourselves to handle one logic (used for NLWO for example)
            if (!moduleWithEndpoint.endpoints) {
                const { id, ...additionalInfo } = moduleWithEndpoint;
                moduleWithEndpoint = { id, endpoints: [additionalInfo] };
            }

            // add additional info from the getConfigs to the device module
            if (currModule) {
                DevicesModule.addDeviceInfo({ deviceId: currModule, additionalInfo: moduleWithEndpoint });
            }
        });

        // add "full_feature_supported" info on products found in the getConfigs
        this.modules_bridged.forEach((moduleId) => {
            const moduleObject = DevicesModule.getDeviceById(moduleId);
            if (['Z3L', 'Z3V'].includes(moduleObject?.type || '')) {
                const moduleConfig = configs.data.body.home.modules.find((_moduleConfig: any) => _moduleConfig.id === moduleId);
                if (moduleConfig) {
                    DevicesModule.addDeviceInfo({
                        deviceId: moduleId,
                        additionalInfo: { full_feature_supported: moduleConfig.full_feature_supported }
                    });
                }
            }
        });
    }

    get isSwitchDatabaseDisabled() {
        if (!this.device_group) {
            return { value: true };
        }

        if (ConfigModule.environment === 'production') {
            const deviceGroupName = DeviceGroupModule.deviceGroupById(this.device_group)?.name || '';
            return {
                value: this.switchDatabaseAllowedGroups.indexOf(deviceGroupName) < 0,
                reason: `Device group "${deviceGroupName}" not allowed to switch database`,
            };
        } else {
            return { value: false };
        }
    }
}
